// src/components/SupportAdminReports.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaChartBar, FaCheckCircle, FaStar, FaTicketAlt,
  FaCommentDots, FaSyncAlt, FaRegClock, FaMedal,
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import './SupportAdminReports.css';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip, Legend, Filler
);

// ─── Constantes ──────────────────────────────────────────────────────────────
const PERIODS = [
  { label: '7 dias',  value: '7d'  },
  { label: '30 dias', value: '30d' },
  { label: '90 dias', value: '90d' },
  { label: 'Total',   value: ''    },
];

const STATUS_CFG = {
  'Aberto':             { color: '#3b82f6', rgb: '59,130,246',  label: 'Abertos'       },
  'Aguardando Suporte': { color: '#f59e0b', rgb: '245,158,11',  label: 'Ag. Suporte'   },
  'Aguardando Cliente': { color: '#8b5cf6', rgb: '139,92,246',  label: 'Ag. Cliente'   },
  'Resolvido':          { color: '#10b981', rgb: '16,185,129',  label: 'Resolvidos'    },
  'Fechado':            { color: '#64748b', rgb: '100,116,139', label: 'Fechados'      },
};

const CHART_PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
];

const NOTA_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981'];

const authCfg = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const qs = (period) => (period ? `?periodo=${period}` : '');

const toNum = (v) => Number(v) || 0;

// ─── Sub-componentes ─────────────────────────────────────────────────────────

/** Esqueleto animado para loading */
const Skeleton = ({ w = '100%', h = 20, radius = 6 }) => (
  <div className="sk-pulse" style={{ width: w, height: h, borderRadius: radius }} />
);

/** Card de KPI */
const KpiCard = ({ title, value, suffix = '', icon, accent, sub, delay = 0 }) => (
  <div className="kpi-card" style={{ '--accent': accent, animationDelay: `${delay}ms` }}>
    <div className="kpi-card-bar" />
    <div className="kpi-icon-wrap" style={{ background: `rgba(${hexToRgb(accent)},.12)`, color: accent }}>
      {icon}
    </div>
    <div className="kpi-body">
      <div className="kpi-value-row">
        <span className="kpi-value">{value ?? '—'}</span>
        {suffix && <span className="kpi-suffix">{suffix}</span>}
      </div>
      <span className="kpi-title">{title}</span>
      {sub && <span className="kpi-sub">{sub}</span>}
    </div>
  </div>
);

/** Barra de nota CSAT */
const StarBar = ({ nota, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="star-bar">
      <span className="sb-label">{nota}★</span>
      <div className="sb-track">
        <div className="sb-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="sb-count">{count}</span>
    </div>
  );
};

// ─── Helpers CSS ──────────────────────────────────────────────────────────────
function hexToRgb(hex = '#000') {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? `${parseInt(r[1], 16)},${parseInt(r[2], 16)},${parseInt(r[3], 16)}` : '0,0,0';
}

const stars = (n) => '★'.repeat(Math.max(0, Math.round(n))) + '☆'.repeat(Math.max(0, 5 - Math.round(n)));

// ─── Componente principal ────────────────────────────────────────────────────
export default function SupportAdminReports() {
  const [period,       setPeriod]       = useState('30d');
  const [refreshing,   setRefreshing]   = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [dashboard,    setDashboard]    = useState(null);
  const [statuses,     setStatuses]     = useState(null);
  const [csatStats,    setCsatStats]    = useState(null);
  const [agentRank,    setAgentRank]    = useState([]);
  const [comments,     setComments]     = useState([]);

  const fetchAll = useCallback(async (toast_ = false) => {
    setRefreshing(true);
    const q = qs(period);
    const [dash, st, csat, ag, cmt] = await Promise.allSettled([
      api.get('/api/admin/suporte-config/reports/main-dashboard', authCfg()),
      api.get('/api/admin/suporte/chamados/stats',                authCfg()),
      api.get(`/api/admin/suporte/satisfacao/stats${q}`,          authCfg()),
      api.get(`/api/admin/suporte/satisfacao/por-atendente${q}`,  authCfg()),
      api.get(`/api/admin/suporte/satisfacao/comentarios${q}`,    authCfg()),
    ]);
    if (dash.status  === 'fulfilled') setDashboard(dash.value.data);
    if (st.status    === 'fulfilled') setStatuses(st.value.data);
    if (csat.status  === 'fulfilled') setCsatStats(csat.value.data);
    if (ag.status    === 'fulfilled') setAgentRank(ag.value.data  || []);
    if (cmt.status   === 'fulfilled') setComments(cmt.value.data  || []);
    setLoading(false);
    setRefreshing(false);
    if (toast_) toast.success('Dados atualizados!');
  }, [period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Derivações ────────────────────────────────────────────────────────────
  const totalTickets   = statuses ? Object.values(statuses).reduce((a, b) => a + b, 0) : 0;
  const openTickets    = statuses
    ? (statuses['Aberto'] || 0) + (statuses['Aguardando Suporte'] || 0) + (statuses['Aguardando Cliente'] || 0)
    : null;

  // ── Chart data ─────────────────────────────────────────────────────────────
  const volumeData = dashboard?.charts?.volume_ultimos_30dias
    ? {
        ...dashboard.charts.volume_ultimos_30dias,
        datasets: dashboard.charts.volume_ultimos_30dias.datasets.map((ds, i) => ({
          ...ds,
          borderColor:     CHART_PALETTE[i],
          backgroundColor: `rgba(${hexToRgb(CHART_PALETTE[i])},.10)`,
          fill:            true,
          tension:         0.42,
          pointRadius:     2.5,
          pointHoverRadius:5,
          pointBackgroundColor: CHART_PALETTE[i],
          borderWidth:     2,
        })),
      }
    : null;

  const categoryData = dashboard?.charts?.top_categorias
    ? {
        ...dashboard.charts.top_categorias,
        datasets: [{
          ...dashboard.charts.top_categorias.datasets[0],
          backgroundColor: CHART_PALETTE.map(c => `${c}cc`),
          borderRadius:    6,
          borderSkipped:   false,
          barThickness:    22,
        }],
      }
    : null;

  const csatDoughnut = csatStats && toNum(csatStats.total) > 0
    ? {
        labels:   ['1★','2★','3★','4★','5★'],
        datasets: [{
          data:            [1,2,3,4,5].map(n => toNum(csatStats.notas?.[n])),
          backgroundColor: NOTA_COLORS,
          borderWidth:     0,
          hoverOffset:     8,
        }],
      }
    : null;

  const baseOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, pointStyle: 'circle', padding: 14, font: { size: 11, family: "'DM Sans', sans-serif" }, color: '#64748b' },
      },
      tooltip: { backgroundColor: '#0f172a', titleFont: { size: 12 }, bodyFont: { size: 11 }, padding: 10, cornerRadius: 8 },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } },
      y: { grid: { color: 'rgba(148,163,184,.12)' }, ticks: { color: '#94a3b8', font: { size: 10 } } },
    },
  };

  const hBarOpts = {
    ...baseOpts,
    indexAxis: 'y',
    scales: {
      x: { ...baseOpts.scales.x, grid: { color: 'rgba(148,163,184,.12)' } },
      y: { ...baseOpts.scales.y, grid: { display: false } },
    },
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="sar-root">
        <div className="sar-header">
          <div className="sar-header-left">
            <div className="sar-title-icon"><FaChartBar /></div>
            <div>
              <h1>Relatórios de Suporte</h1>
              <p>Carregando indicadores…</p>
            </div>
          </div>
        </div>
        <div className="kpi-grid">
          {Array.from({length: 4}).map((_, i) => (
            <div key={i} className="kpi-card kpi-skeleton">
              <Skeleton w={46} h={46} radius={12} />
              <div style={{flex:1, display:'flex', flexDirection:'column', gap:8}}>
                <Skeleton h={28} w="60%" />
                <Skeleton h={13} w="80%" />
              </div>
            </div>
          ))}
        </div>
        <div className="charts-row">
          <div className="chart-card" style={{flex:1.8}}><Skeleton h={240} radius={10} /></div>
          <div className="chart-card" style={{flex:1}}><Skeleton h={240} radius={10} /></div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="sar-root">

      {/* ══ HEADER ══ */}
      <header className="sar-header">
        <div className="sar-header-left">
          <div className="sar-title-icon"><FaChartBar /></div>
          <div>
            <h1>Relatórios de Suporte</h1>
            <p>Desempenho operacional e satisfação dos usuários</p>
          </div>
        </div>
        <div className="sar-header-right">
          <div className="period-tabs">
            {PERIODS.map(p => (
              <button
                key={p.value}
                className={`period-btn ${period === p.value ? 'active' : ''}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={() => fetchAll(true)}
            disabled={refreshing}
            title="Atualizar"
          >
            <FaSyncAlt />
          </button>
        </div>
      </header>

      {/* ══ KPI CARDS ══ */}
      <div className="kpi-grid">
        <KpiCard
          title="Chamados em Aberto"
          value={openTickets}
          icon={<FaTicketAlt />}
          accent="#3b82f6"
          sub={totalTickets > 0 ? `de ${totalTickets} no total` : undefined}
          delay={0}
        />
        <KpiCard
          title="SLA de Resolução"
          value={dashboard?.kpis?.sla_performance}
          suffix="%"
          icon={<FaCheckCircle />}
          accent="#10b981"
          sub="dentro do prazo"
          delay={60}
        />
        <KpiCard
          title="Satisfação (CSAT)"
          value={csatStats?.media != null ? Number(csatStats.media).toFixed(1) : '—'}
          suffix="/5"
          icon={<FaStar />}
          accent="#f59e0b"
          sub={csatStats?.total ? `${csatStats.total} avaliações` : undefined}
          delay={120}
        />
        <KpiCard
          title="Taxa de Resposta CSAT"
          value={csatStats?.taxa_resposta}
          suffix="%"
          icon={<FaCommentDots />}
          accent="#8b5cf6"
          sub="usuários que avaliaram"
          delay={180}
        />
      </div>

      {/* ══ STATUS BREAKDOWN ══ */}
      {statuses && (
        <div className="status-strip">
          {Object.entries(STATUS_CFG).map(([key, cfg]) => {
            const count = statuses[key] || 0;
            const pct   = totalTickets > 0 ? Math.round((count / totalTickets) * 100) : 0;
            return (
              <div key={key} className="status-pill" style={{ '--pc': cfg.color, '--pc-rgb': cfg.rgb }}>
                <span className="sp-dot" />
                <span className="sp-label">{cfg.label}</span>
                <strong className="sp-count">{count}</strong>
                <span className="sp-pct">{pct}%</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ══ CHARTS ROW 1 ══ */}
      <div className="charts-row">
        <div className="chart-card chart-wide">
          <p className="chart-title">
            <span className="ct-dot ct-blue" /> Volume · Abertos vs. Resolvidos
            <span className="ct-period">{PERIODS.find(p => p.value === '30d')?.label}</span>
          </p>
          <div className="chart-wrap">
            {volumeData
              ? <Line data={volumeData} options={baseOpts} />
              : <p className="no-data">Sem dados suficientes</p>}
          </div>
        </div>

        <div className="chart-card">
          <p className="chart-title">
            <span className="ct-dot ct-amber" /> Top 5 Categorias
          </p>
          <div className="chart-wrap">
            {categoryData
              ? <Bar data={categoryData} options={hBarOpts} />
              : <p className="no-data">Sem dados suficientes</p>}
          </div>
        </div>
      </div>

      {/* ══ CHARTS ROW 2 ══ */}
      <div className="charts-row">

        {/* CSAT distribuição */}
        <div className="chart-card csat-card">
          <p className="chart-title">
            <span className="ct-dot ct-gold" /> Distribuição CSAT
          </p>
          {csatDoughnut ? (
            <div className="csat-inner">
              <div className="donut-wrap">
                <Doughnut
                  data={csatDoughnut}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '72%',
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: '#0f172a',
                        callbacks: { label: ctx => ` ${ctx.raw} avaliações` },
                      },
                    },
                  }}
                />
                <div className="donut-center">
                  <span className="dc-score">{Number(csatStats.media).toFixed(1)}</span>
                  <span className="dc-max">/ 5</span>
                </div>
              </div>

              <div className="star-bars-wrap">
                {[5, 4, 3, 2, 1].map((n, i) => (
                  <StarBar
                    key={n}
                    nota={n}
                    count={toNum(csatStats.notas?.[n])}
                    total={toNum(csatStats.total)}
                    color={NOTA_COLORS[4 - i]}
                  />
                ))}
              </div>
            </div>
          ) : (
            <p className="no-data">Sem avaliações no período</p>
          )}
        </div>

        {/* Ranking atendentes */}
        <div className="chart-card">
          <p className="chart-title">
            <span className="ct-dot ct-purple" /> Ranking de Atendentes
          </p>
          {agentRank.length > 0 ? (
            <div className="agent-list">
              {agentRank.slice(0, 6).map((ag, i) => (
                <div key={ag.atribuido_a_id ?? i} className="agent-row">
                  <span className={`ag-pos pos-${i + 1}`}>
                    {i < 3 ? <FaMedal /> : `#${i + 1}`}
                  </span>
                  <div className="ag-info">
                    <span className="ag-name">{ag.atendente_nome}</span>
                    <span className="ag-evals">{ag.total_avaliacoes} aval.</span>
                  </div>
                  <div className="ag-score">
                    <span className="ag-num">{Number(ag.media_nota).toFixed(1)}</span>
                    <span className="ag-stars">{stars(ag.media_nota)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">Sem dados no período</p>
          )}
        </div>

        {/* Comentários recentes */}
        <div className="chart-card">
          <p className="chart-title">
            <span className="ct-dot ct-green" /> Comentários Recentes
          </p>
          {comments.length > 0 ? (
            <div className="comments-list">
              {comments.slice(0, 4).map(c => (
                <div key={c.id} className="comment-item">
                  <div className="ci-header">
                    <span className="ci-author">{c.usuario_nome || 'Anônimo'}</span>
                    <span className="ci-stars" data-nota={Math.round(c.nota)}>
                      {'★'.repeat(Math.max(0, Math.round(c.nota)))}
                    </span>
                  </div>
                  <p className="ci-text">{c.comentario}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">Nenhum comentário no período</p>
          )}
        </div>
      </div>

    </div>
  );
}